import client from "@/client/marketDataApiClient.ts";
import { useQuery, queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import type { GetInstrumentBookFundamentalsQueryResponse, GetInstrumentBookFundamentalsPathParams, GetInstrumentBookFundamentals404 } from "../types/GetInstrumentBookFundamentals";
import type { QueryObserverOptions, UseQueryResult, QueryKey, UseSuspenseQueryOptions, UseSuspenseQueryResult } from "@tanstack/react-query";

 type GetInstrumentBookFundamentalsClient = typeof client<GetInstrumentBookFundamentalsQueryResponse, GetInstrumentBookFundamentals404, never>;
type GetInstrumentBookFundamentals = {
    data: GetInstrumentBookFundamentalsQueryResponse;
    error: GetInstrumentBookFundamentals404;
    request: never;
    pathParams: GetInstrumentBookFundamentalsPathParams;
    queryParams: never;
    headerParams: never;
    response: GetInstrumentBookFundamentalsQueryResponse;
    client: {
        parameters: Partial<Parameters<GetInstrumentBookFundamentalsClient>[0]>;
        return: Awaited<ReturnType<GetInstrumentBookFundamentalsClient>>;
    };
};
export const getInstrumentBookFundamentalsQueryKey = (clientCode: GetInstrumentBookFundamentalsPathParams["clientCode"], marketCode: GetInstrumentBookFundamentalsPathParams["marketCode"], symbol: GetInstrumentBookFundamentalsPathParams["symbol"]) => [{ url: "/api/clients/:clientCode/markets/:marketCode/instruments/:symbol/data/fundamentals", params: { clientCode: clientCode, marketCode: marketCode, symbol: symbol } }] as const;
export type GetInstrumentBookFundamentalsQueryKey = ReturnType<typeof getInstrumentBookFundamentalsQueryKey>;
export function getInstrumentBookFundamentalsQueryOptions(clientCode: GetInstrumentBookFundamentalsPathParams["clientCode"], marketCode: GetInstrumentBookFundamentalsPathParams["marketCode"], symbol: GetInstrumentBookFundamentalsPathParams["symbol"], options: GetInstrumentBookFundamentals["client"]["parameters"] = {}) {
    const queryKey = getInstrumentBookFundamentalsQueryKey(clientCode, marketCode, symbol);
    return queryOptions({
        queryKey,
        queryFn: async () => {
            const res = await client<GetInstrumentBookFundamentals["data"], GetInstrumentBookFundamentals["error"]>({
                method: "get",
                url: `/api/clients/${clientCode}/markets/${marketCode}/instruments/${symbol}/data/fundamentals`,
                ...options
            });
            return res.data;
        },
    });
}
/**
 * @description Returns fundamental information for a client market's instrument as per the
given symbol. This includes the current state of the instrument and some
basic up to day information. If you want the instrument's full depth
information then use the `/full` endpoint.
 * @summary Fundamentals
 * @link /api/clients/:clientCode/markets/:marketCode/instruments/:symbol/data/fundamentals
 */
export function useGetInstrumentBookFundamentals<TData = GetInstrumentBookFundamentals["response"], TQueryData = GetInstrumentBookFundamentals["response"], TQueryKey extends QueryKey = GetInstrumentBookFundamentalsQueryKey>(clientCode: GetInstrumentBookFundamentalsPathParams["clientCode"], marketCode: GetInstrumentBookFundamentalsPathParams["marketCode"], symbol: GetInstrumentBookFundamentalsPathParams["symbol"], options: {
    query?: Partial<QueryObserverOptions<GetInstrumentBookFundamentals["response"], GetInstrumentBookFundamentals["error"], TData, TQueryData, TQueryKey>>;
    client?: GetInstrumentBookFundamentals["client"]["parameters"];
} = {}): UseQueryResult<TData, GetInstrumentBookFundamentals["error"]> & {
    queryKey: TQueryKey;
} {
    const { query: queryOptions, client: clientOptions = {} } = options ?? {};
    const queryKey = queryOptions?.queryKey ?? getInstrumentBookFundamentalsQueryKey(clientCode, marketCode, symbol);
    const query = useQuery({
        ...getInstrumentBookFundamentalsQueryOptions(clientCode, marketCode, symbol, clientOptions) as unknown as QueryObserverOptions,
        queryKey,
        ...queryOptions as unknown as Omit<QueryObserverOptions, "queryKey">
    }) as UseQueryResult<TData, GetInstrumentBookFundamentals["error"]> & {
        queryKey: TQueryKey;
    };
    query.queryKey = queryKey as TQueryKey;
    return query;
}
export const getInstrumentBookFundamentalsSuspenseQueryKey = (clientCode: GetInstrumentBookFundamentalsPathParams["clientCode"], marketCode: GetInstrumentBookFundamentalsPathParams["marketCode"], symbol: GetInstrumentBookFundamentalsPathParams["symbol"]) => [{ url: "/api/clients/:clientCode/markets/:marketCode/instruments/:symbol/data/fundamentals", params: { clientCode: clientCode, marketCode: marketCode, symbol: symbol } }] as const;
export type GetInstrumentBookFundamentalsSuspenseQueryKey = ReturnType<typeof getInstrumentBookFundamentalsSuspenseQueryKey>;
export function getInstrumentBookFundamentalsSuspenseQueryOptions(clientCode: GetInstrumentBookFundamentalsPathParams["clientCode"], marketCode: GetInstrumentBookFundamentalsPathParams["marketCode"], symbol: GetInstrumentBookFundamentalsPathParams["symbol"], options: GetInstrumentBookFundamentals["client"]["parameters"] = {}) {
    const queryKey = getInstrumentBookFundamentalsSuspenseQueryKey(clientCode, marketCode, symbol);
    return queryOptions({
        queryKey,
        queryFn: async () => {
            const res = await client<GetInstrumentBookFundamentals["data"], GetInstrumentBookFundamentals["error"]>({
                method: "get",
                url: `/api/clients/${clientCode}/markets/${marketCode}/instruments/${symbol}/data/fundamentals`,
                ...options
            });
            return res.data;
        },
    });
}
/**
 * @description Returns fundamental information for a client market's instrument as per the
given symbol. This includes the current state of the instrument and some
basic up to day information. If you want the instrument's full depth
information then use the `/full` endpoint.
 * @summary Fundamentals
 * @link /api/clients/:clientCode/markets/:marketCode/instruments/:symbol/data/fundamentals
 */
export function useGetInstrumentBookFundamentalsSuspense<TData = GetInstrumentBookFundamentals["response"], TQueryKey extends QueryKey = GetInstrumentBookFundamentalsSuspenseQueryKey>(clientCode: GetInstrumentBookFundamentalsPathParams["clientCode"], marketCode: GetInstrumentBookFundamentalsPathParams["marketCode"], symbol: GetInstrumentBookFundamentalsPathParams["symbol"], options: {
    query?: Partial<UseSuspenseQueryOptions<GetInstrumentBookFundamentals["response"], GetInstrumentBookFundamentals["error"], TData, TQueryKey>>;
    client?: GetInstrumentBookFundamentals["client"]["parameters"];
} = {}): UseSuspenseQueryResult<TData, GetInstrumentBookFundamentals["error"]> & {
    queryKey: TQueryKey;
} {
    const { query: queryOptions, client: clientOptions = {} } = options ?? {};
    const queryKey = queryOptions?.queryKey ?? getInstrumentBookFundamentalsSuspenseQueryKey(clientCode, marketCode, symbol);
    const query = useSuspenseQuery({
        ...getInstrumentBookFundamentalsSuspenseQueryOptions(clientCode, marketCode, symbol, clientOptions) as unknown as QueryObserverOptions,
        queryKey,
        ...queryOptions as unknown as Omit<QueryObserverOptions, "queryKey">
    }) as UseSuspenseQueryResult<TData, GetInstrumentBookFundamentals["error"]> & {
        queryKey: TQueryKey;
    };
    query.queryKey = queryKey as TQueryKey;
    return query;
}